<template>
  <div>
    <div class="row setup">
      <div class="col-12 col-md-6">
        <div class="row selections">
          <div v-if="!customized && themes && themes.length" class="col-12">
            <h5 class="section-title mt-4 mt-md-0">
              Select a Theme
            </h5>

            <div v-if="default_themes && default_themes.length" class="list-group mt-4">
              <button v-for="(item, index) in default_themes" :key="index"
                      type="button" class="list-group-item list-group-item-action"
                      :class="{active : selected_theme && selected_theme.id === item.id}"
                      @click="selectCustomTheme(item.id)">
                <strong>{{ item.name }}</strong><br>
                <p>{{ item.description }}</p>
              </button>
            </div>

            <ul v-if="custom_themes.length" class="list-group mt-2">
              <li v-for="item in custom_themes" :key="item.id"
                  class="list-group-item" :class="{active : selected_theme && selected_theme.id === item.id}">
                <div class="clearfix">
                  <span class="float-left pointer" @click="selectCustomTheme(item.id)">
                    <strong>{{ item.name }}</strong>
                    <span v-if="item.locked">&nbsp;<i class="fa fa-lock" /></span>
                  </span>
                  <span class="float-right">
                    <div class="dropdown dropdown-inline">
                      <button type="button" class="btn btn-ss btn-icon btn-sm btn-icon-sm"
                              data-toggle="dropdown"
                              aria-haspopup="true" aria-expanded="false">
                        <i class="flaticon-more" />
                      </button>
                      <div class="dropdown-menu dropdown-menu-right">
                        <button class="dropdown-item" data-toggle="modal"
                                data-target="#edit_theme" @click="setEditThemeModalData(item.id)"><i class="la la-edit" /> Edit </button>

                        <button v-if="!item.is_default"
                                class="dropdown-item"
                                @click="makeDefaultTheme(item)">
                          <i class="la la-flag-o" /> Make Default
                        </button>

                        <button v-else
                                class="dropdown-item"
                                @click="removeDefaultTheme(item)">
                          <i class="la la-flag-o" /> Remove Default
                        </button>

                        <div v-if="canToggleLock">
                          <button v-if="item.locked" class="dropdown-item" @click="toggleLock(item, false)"><i class="fa fa-lock" /> Unlock Theme</button>
                          <button v-else class="dropdown-item" @click="toggleLock(item, true)"><i class="fa fa-lock-open" /> Lock Theme</button>
                        </div>
                        <button class="dropdown-item" @click="toggleCustomized"><i class="la la-edit" /> Customize </button>
                        <div class="dropdown-divider" />
                        <button class="dropdown-item" @click="deleteTheme(item.id)"><i class="la la-trash" /> Delete </button>
                      </div>
                    </div>
                  </span>
                </div>
                <p class="pointer" @click="selectCustomTheme(item.id)">
                  {{ item.description }}
                </p>
              </li>
            </ul>
            <div v-else>
              <p>You don't have any custom themes yet.</p>
            </div>

            <div class="mt-4">
              <button class="btn btn-ss kt-btn--icon mb-2" @click="toggleCustomized">
                <span><i class="la la-edit" /> Customize</span>
              </button>
            </div>

            <textarea id="embed_code" v-model="embed_code"
                      class="form-control kt-input"
                      readonly rows="5" />
            <button class="btn btn-ss kt-btn--icon mt-2"
                    @click.stop.prevent="copyValueToClipboard(embed_code)">
              <span>
                <i class="la la-copy" /><span>Copy to Clipboard</span>
              </span>
            </button>
          </div>
          <div v-if="customized" class="col-12">
            <div class="row">
              <div class="col-12 mt-4 mt-md-0">
                <h5 class="section-title">
                  Customize {{ selected_theme.name }}
                </h5>
                <div class="kt-form__group form-group">
                  <multiselect v-model="selected_theme.layout" :close-on-select="true"
                               :options="layouts"
                               track-by="key" label="name"
                               :allow-empty="false" />
                </div>

                <div class="kt-form__group form-group">
                  <div class="d-flex flex-column">
                    <ss-checkbox id="selected-theme-artwork" v-model="selected_theme.settings.artwork">
                      Show Artwork
                    </ss-checkbox>
                    <ss-checkbox id="selected-theme-description" v-model="selected_theme.settings.description">
                      Show Description
                    </ss-checkbox>
                    <ss-checkbox id="selected-theme-download" v-model="selected_theme.settings.download">
                      Show Download
                    </ss-checkbox>
                    <ss-checkbox id="selected-theme-sharing" v-model="selected_theme.settings.sharing">
                      Show Share
                    </ss-checkbox>
                    <ss-checkbox id="selected_theme-waveform" v-model="selected_theme.settings.waveform">
                      Show Waveform
                    </ss-checkbox>
                    <ss-checkbox id="selected_theme-chapters" v-model="selected_theme.settings.chapters">
                      Show Chapters
                    </ss-checkbox>
                  </div>
                </div>
                <div class="form-group ">
                  <label>Border Radius</label>
                  <div class="input-group">
                    <input v-model="selected_theme.settings.rounded" type="number"
                           class="form-control"
                           placeholder="Units" aria-describedby="basic-addon1">
                    <div class="input-group-append">
                      <span id="basic-addon1" class="input-group-text">px</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-12">
                <div class="kt-portlet kt-portlet--bordered kt-portlet--tabs">
                  <div class="kt-portlet__head">
                    <div class="kt-portlet__head-label">
                      <h3 class="kt-portlet__head-title">
                        Colours
                      </h3>
                    </div>
                    <div class="kt-portlet__head-toolbar">
                      <ul class="nav nav-tabs nav-tabs-line nav-tabs-line-right" role="tablist">
                        <li class="nav-item">
                          <a class="nav-link active" data-toggle="tab"
                             href="#background_tab_content"
                             role="tab">
                            Background
                          </a>
                        </li>
                        <li class="nav-item">
                          <a class="nav-link" data-toggle="tab"
                             href="#border_tab_content"
                             role="tab">
                            Border
                          </a>
                        </li>
                        <li class="nav-item">
                          <a class="nav-link" data-toggle="tab"
                             href="#accent_tab_content"
                             role="tab">
                            Accent
                          </a>
                        </li>
                        <li class="nav-item">
                          <a class="nav-link" data-toggle="tab"
                             href="#text_tab_content"
                             role="tab">
                            Text
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div class="kt-portlet__body">
                    <div class="tab-content">
                      <div id="background_tab_content" class="tab-pane active" role="tabpanel">
                        <ss-checkbox id="selected-theme-background-colour-default" v-model="selected_theme.settings.background_colour.default">
                          Default
                        </ss-checkbox>

                        <chrome-picker v-if="!selected_theme.settings.background_colour.default" v-model="selected_theme.settings.background_colour"
                                       :value="selected_theme.settings.background_colour" />
                      </div>
                      <div id="border_tab_content" class="tab-pane" role="tabpanel">
                        <ss-checkbox id="selected-theme-border-colour-default" v-model="selected_theme.settings.border_colour.default">
                          Default
                        </ss-checkbox>

                        <chrome-picker v-if="!selected_theme.settings.border_colour.default" v-model="selected_theme.settings.border_colour"
                                       :value="selected_theme.settings.border_colour" />
                      </div>
                      <div id="accent_tab_content" class="tab-pane" role="tabpanel">
                        <ss-checkbox id="selected-theme-accent-colour-default" v-model="selected_theme.settings.accent_colour.default">
                          Default
                        </ss-checkbox>

                        <chrome-picker v-if="!selected_theme.settings.accent_colour.default" v-model="selected_theme.settings.accent_colour"
                                       :value="selected_theme.settings.accent_colour" />
                      </div>
                      <div id="text_tab_content" class="tab-pane" role="tabpanel">
                        <ss-checkbox id="selected-theme-text-colour-default" v-model="selected_theme.settings.text_colour.default">
                          Default
                        </ss-checkbox>

                        <chrome-picker v-if="!selected_theme.settings.text_colour.default" v-model="selected_theme.settings.text_colour"
                                       :value="selected_theme.settings.text_colour" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-12">
                <hr class="my-4">

                <button id="save_embed_modal_button"
                        class="btn btn-ss-standout kt-btn--icon mb-2"
                        @click="showSaveModal">
                  <i class="la la-plus-circle" />
                  Save as Theme
                </button>

                <button v-if="selected_theme && selected_theme.is_custom" id="update_theme_to_match"
                        class="btn btn-ss mb-2"
                        :disabled="selected_theme.locked === 1" @click="updateCustomThemeToMatch">
                  <span v-if="selected_theme.locked === 1"><i class="fa fa-lock" />&nbsp;</span>
                  <span v-else><i class="la la-save" /></span> Update Theme to Match
                </button>

                <button class="btn btn-ss kt-btn--icon mb-2" @click="updatePreview">
                  <span><i class="la la-refresh" /> Update Preview</span>
                </button>

                <button class="btn btn-ss kt-btn--icon mb-2" @click="toggleCustomized">
                  <span><i class="la la-arrow-circle-left" /> Back</span>
                </button>

                <confirm-modal :name="modal_name" title="Save Embed Settings as Theme" confirm-text="Save">
                  <div class="kt-form__group form-group">
                    <input id="custom_theme_name" v-model="new_theme.name"
                           type="text" class="form-control kt-input"
                           placeholder="Theme Name...">
                  </div>

                  <div class="kt-form__group form-group">
                    <textarea id="custom_theme_description" v-model="new_theme.description"
                              class="form-control kt-input" rows="5"
                              placeholder="Theme Description..." />
                  </div>
                </confirm-modal>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12 col-md-6">
        <h5 class="section-title mt-4 mt-md-0">
          <span>
            Preview <button class="btn btn-ss btn-sm btn-icon mb-2" @click="updatePreview">
              <i class="la la-refresh" />
            </button>
          </span>
        </h5>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-if="preview_code" id="player_iframe_preview" v-html="preview_code" />
      </div>
    </div>

    <!-- Edit Theme Modal -->

    <div id="edit_theme" ref="edit_modal"
         class="modal fade" tabindex="-1"
         role="dialog" aria-labelledby="ariaModalLabel"
         aria-hidden="true">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Edit Theme {{ edit_theme_data.name }}
            </h5>
            <button type="button" class="close"
                    data-dismiss="modal"
                    aria-label="Close"
                    @click="closeModal()">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div class="kt-form__group form-group">
              <input v-model="edit_theme_data.name" type="text"
                     class="form-control kt-input" placeholder="Theme Name...">
            </div>

            <div class="kt-form__group form-group">
              <textarea v-model="edit_theme_data.description"
                        class="form-control kt-input" rows="5"
                        placeholder="Theme Description..." />
            </div>
          </div>
          <div class="modal-footer">
            <slot name="footer">
              <button type="button" class="btn btn-ss"
                      data-dismiss="modal" @click="edit_theme_data = {}; closeModal();">
                Close
              </button>
              <button type="button" class="btn btn-ss"
                      @click="updateCustomTheme">
                Update Theme
              </button>
            </slot>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Chrome } from "vue-color";
import ConfirmModal from "VueModals/ConfirmModal";

export default {
  components: {
    ConfirmModal,
    "chrome-picker": Chrome,
  },

  props: {
    playerUrl: {
      type: String,
      required: true,
    },
    isFeed: {
      type: Boolean,
      required: true,
    },
  },
  data: () => ({
    customized: false,
    selected_theme: null,
    themes: [],
    new_theme: {
      name: null,
      description: null,
    },
    layouts: [
      {
        name: "Slim",
        key: "legacy",
        description: "Slim Player",
      },
      {
        name: "Square",
        key: "square",
        description: "Large square layout",
      },
    ],
    // customisations: {},
    edit_theme_data: {},
    preview_code: null,
    modal_name: `save-embed-theme`,
  }),
  computed: {
    custom_themes() {
      return _.filter(this.themes, theme => theme.is_custom);
    },
    default_themes() {
      return _.filter(this.themes, theme => !theme.is_custom);
    },
    canToggleLock() {
      return this.permissionsIsOrganisationAdmin();
    },
    embed_code() {
      if (!this.selected_theme) {
        return null;
      }

      let layout = 'square';
      if (this.selected_theme.layout && _.isObject(this.selected_theme.layout)) {
        layout = this.selected_theme.layout.key;
      }

      let params = `?layout=${layout}`;

      if (this.selected_theme && this.selected_theme.is_custom) {
        params += `&custom_theme=${this.selected_theme.id}`;
      }

      if (this.selected_theme.settings && this.customized) {
        _.forEach(this.selected_theme.settings, (value, key) => {
          if (key.includes("_colour")) {
            key = key.replace("_colour", '');
            // If default then don't pass the param
            if (!value.default && value.hex) {
              const colour = value.hex.replace("#", "");
              params += `&${key}=${colour}`;
            }
          } else {
            params += `&${key}=${value}`;
          }
        });
      }

      const height = layout === 'square' ? '700' : '150';
      const width = layout === 'square' ? '700' : '100%';

      return `<iframe src="${this.playerUrl}${params}" width=${width} height=${height} allow="clipboard-read; clipboard-write" frameborder="0" ></iframe>`;
    },
  },
  beforeMount() {
    // this.customisations = this.getDefaultCustomisations();
    this.getThemes();
  },
  mounted() {
    EventBus.$on('embed_preview_type_updated', () => {
      this.updatePreview();
    });
  },
  methods: {
    async showSaveModal() {
      try {
        const modal_res = await this.fireModal(this.modal_name);
        if (modal_res.data.confirm) {
          this.saveAsTheme();
        }
      } catch (err) {
        window.handleAxiosError(err);
      }
    },
    toggleCustomized() {
      this.customized = !this.customized;
    },
    closeModal() {
      $('#edit_theme').modal('hide');
    },
    toggleLock(theme, lock) {
      const url = `/api/v1/custom-themes/${theme.id}/${lock ? 'lock' : 'unlock'}`;
      axios.patch(url).then((response) => {
        if (response.data.data && response.data.data.id) {
          const theme_to_update_index = _.findIndex(this.themes, item => item.id === response.data.data.id);

          Vue.set(this.themes, theme_to_update_index, response.data.data);

          toastr.success(`Theme has been ${lock ? 'locked' : 'unlocked'}.`);
        }
      }).catch((error) => {
        window.handleAxiosError(error);
      });
    },
    updatePreview() {
      // Create a non-reactive copy of the embed code
      const preview_code = JSON.parse(JSON.stringify(this.embed_code));
      this.preview_code = {};
      Vue.nextTick(() => this.preview_code = preview_code);
    },
    selectCustomTheme(id) {
      this.selected_theme = _.find(this.themes, theme => theme.id === id);

      this.updatePreview();
    },
    getNonReactiveLayoutInstance(layout_key) {
      const layout = _.find(this.layouts, {
        key: layout_key,
      });
      return JSON.parse(JSON.stringify(layout));
    },
    getThemes() {
      axios
        .get("/api/v1/custom-themes")
        .then((response) => {
          this.themes = [
            {
              id: 'default',
              name: "Default",
              key: "default",
              description: "Default Colours",
              is_custom: false,
              settings: {
                rounded: 0,
                artwork: 1,
                sharing: 0,
                download: 0,
                description: 0,
                waveform: true,
                background_colour: {
                  default: true,
                },
                border_colour: {
                  default: true,
                },
                text_colour: {
                  default: true,
                },
                accent_colour: {
                  default: true,
                },
              },
              layout: this.getNonReactiveLayoutInstance('square'),
            },
          ];

          const custom_themes = response.data.data.themes;
          const vm = this;
          _.forEach(custom_themes, (custom_theme) => {
            // Assign a non-reactive instance of the layout object
            custom_theme.layout = vm.getNonReactiveLayoutInstance(custom_theme.layout);
            custom_theme.is_custom = true;
            vm.themes.push(custom_theme);
          });

          const default_theme = _.find(this.themes, theme => theme.is_default);

          if (default_theme) {
            this.selected_theme = default_theme;
          } else {
            this.selected_theme = this.themes[0];
          }

          this.updatePreview();
        }).catch((error) => {
          window.handleAxiosError(error);
        });
    },
    async saveAsTheme() {
      try {
        const response = await axios.post("/api/v1/custom-themes", {
          settings: this.selected_theme.settings,
          layout: this.selected_theme.layout.key,
          name: this.new_theme.name,
          description: this.new_theme.description,
        });
        const new_theme = response.data.data.theme;
        new_theme.is_custom = true;
        this.themes.push(new_theme);
        this.customized = false;
        // this.customisations = this.getDefaultCustomisations();
        this.selectCustomTheme(response.data.data.theme.id);
      } catch (error) {
        window.handleAxiosError(error);
      }
    },
    deleteTheme(theme_id) {
      axios
        .delete(`/api/v1/custom-themes/${theme_id}`)
        .then(() => {
          toastr.success("Deleted Theme");
          this.custom_themes = _.filter(this.custom_themes, theme => theme.id !== theme_id);
        })
        .catch((error) => {
          window.handleAxiosError(error);
        });
    },
    updateCustomThemeToMatch() {
      const vm = this;
      if (this.selected_theme) {
        axios
          .put(`/api/v1/custom-themes/${this.selected_theme.id}`, {
            settings: this.selected_theme.settings,
            layout: this.selected_theme.layout.key,
          })
          .then((response) => {
            toastr.success("Theme Updated");
            if (response.data.data) {
              // @todo : update theme object
              vm.customized = false;

              this.updatePreview();
            }
          })
          .catch((error) => {
            window.handleAxiosError(error);
          });
      } else {
        toastr.error("You have not selected a custom theme");
      }
    },
    setEditThemeModalData(custom_theme_id) {
      const theme_to_edit = _.find(this.custom_themes, theme => theme.id === custom_theme_id);

      this.edit_theme_data = {
        id: theme_to_edit.id,
        name: theme_to_edit.name,
        description: theme_to_edit.description,
      };
    },
    updateCustomTheme() {
      const data = {
        name: this.edit_theme_data.name,
        description: this.edit_theme_data.description,
      };
      axios
        .put(`/api/v1/custom-themes/${this.edit_theme_data.id}`, data)
        .then((response) => {
          toastr.success("Updated Theme");

          const new_data = response.data.data.theme;

          const theme_to_update_index = _.findIndex(
            this.custom_themes,
            theme => theme.id === new_data.id,
          );

          Vue.set(this.custom_themes, theme_to_update_index, new_data);

          this.updatePreview();
        })
        .catch((error) => {
          console.error(error);
          window.handleAxiosError(error);
        });
    },
    makeDefaultTheme(theme) {
      axios.patch(`/api/v1/custom-themes/${theme.id}/make-default`).then(() => {
        const nowDefault = !theme.is_default;
        theme.is_default = nowDefault;

        if (nowDefault) {
          // ensure every other theme is NOT default
          this.custom_themes.forEach((t) => {
            if (t.id !== theme.id) t.is_default = false;
          });
          toastr.success(`${theme.name} has been made default`);
        } else {
          toastr.success(`${theme.name} default removed`);
        }
      }).catch(window.handleAxiosError);
    },

    removeDefaultTheme(theme) {
      this.makeDefaultTheme(theme);
    },
  },
};
</script>

<style lang="scss">
@use "sass:map";
@use "~AssetsSass/_variables.scss" as *;

.kt-tabs-line .kt-tabs__item {
    margin-right: 20px;
}

.list-group-item.active {
    background-color: map.get($ss, 'primary');
    border-color: map.get($ss, 'primary');
}

.kt-switch input:checked ~ span:after {
    background-color: map.get($ss, 'primary');
}

.kt-tabs-line.kt-tabs-line--2x a.kt-tabs__link:hover,
.kt-tabs-line.kt-tabs-line--2x a.kt-tabs__link.active {
    border-bottom-color: map.get($ss, 'primary') !important;
}

.theme-dropdown-button {
    padding: 0.2rem;
}

.pointer {
    cursor: pointer;
}

.section-title {
    padding-bottom: 0.5rem !important;
    margin-bottom: 1.5rem !important;
    border-bottom: 1px solid rgb(235, 236, 241);
}

@media (min-width: 768px) {
    .selections {
        border-right: 1px solid #ebecf1;
    }
}
</style>
